import React, { useMemo, useContext, useState } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { required } from '@ubeya/shared/utils/form';
import useFileTypes from '@ubeya/shared/hooks/generic/useFileTypes';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  Body,
  Table,
  EditableSelect,
  DeleteRenderer,
  ExtraSmall,
  TextWithTooltip,
  FlexMiddle,
  PdfModal
} from '@ubeya/shared-web/components';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import { getSource } from '@ubeya/shared-web/utils/files';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import AddDocument from './AddDocument';

const SubTitle = styled(Body)`
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const SmallText = styled(ExtraSmall)`
  color: ${({ theme }) => theme.colors.grey4};
  ${({ onClick }) =>
    !!onClick &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `};
`;

const Link = styled.a``;

const SmallTextRow = styled(FlexMiddle)`
  gap: 5px;
`;

const DocumentsTable = () => {
  const { t } = useContext(ConfigContext);
  const { fileTypes } = useFileTypes();
  const { data, handleDelete, handleEdit } = useArrayManagement('documents');
  const { dateFormat } = useContext(ConfigContext);
  const [openPdf, setOpenPdf] = useState();
  const { mappedAdmins } = useAdmins();

  const dataWithDummy = useMemo(() => [...data, {}], [data]);

  const columns = useMemo(
    () => [
      {
        Header: t('fileName'),
        dataKey: 'name',
        Cell: ({ rowData }) => {
          if (!rowData.id) {
            return <AddDocument small />;
          }

          const extension = rowData.title.split('.').slice(-1).pop()?.toLowerCase();

          return (
            <>
              <TextWithTooltip>{rowData.title}</TextWithTooltip>
              <SmallTextRow>
                <SmallText>{extension}</SmallText>
                {extension === 'pdf' && (
                  <SmallText onClick={() => setOpenPdf(getSource(rowData))}>{t('view')}</SmallText>
                )}
                <SmallText as={Link} href={getSource(rowData)} target="_blank">
                  {t('download')}
                </SmallText>
              </SmallTextRow>
            </>
          );
        }
      },
      {
        Header: t('type'),
        dataKey: 'typeId',
        Cell: (props) => (props.rowData.id ? <EditableSelect {...props} /> : <></>),
        columnData: ({ rowData }) => ({
          validate: required,
          options: fileTypes,
          onChange: (value) => handleEdit(rowData.id, { typeId: value })
        })
      },
      {
        Header: t('uploadDate'),
        dataKey: 'date',
        formatter: (value, { id }) => (!id ? '' : value ? moment(value).format(dateFormat) : '-'),
        maxWidth: 140
      },
      {
        Header: t('uploadedBy'),
        dataKey: 'createdBy',
        formatter: (value) =>
          value
            ? [mappedAdmins[value]?.firstName, mappedAdmins[value]?.lastName].filter((curr) => !!curr).join(' ')
            : '',
        maxWidth: 140
      }
    ],
    [dateFormat, fileTypes, handleEdit, mappedAdmins, t]
  );

  return (
    <>
      {data.length === 0 && <SubTitle>{t('addFirstDocuments')}</SubTitle>}

      {data.length > 0 && (
        <Table
          columns={columns}
          data={dataWithDummy}
          autoHeight
          headerRowHeight={50}
          shadow={false}
          actionRenderer={({ rowData }) =>
            rowData.id ? <DeleteRenderer onDelete={() => handleDelete(rowData.id)} /> : <></>
          }
        />
      )}

      {data.length === 0 && <AddDocument />}
      {!!openPdf && <PdfModal url={openPdf} onClose={() => setOpenPdf(undefined)} />}
    </>
  );
};

export default React.memo(DocumentsTable);
